import { vaultService } from '../vaultService';
import { cacheVaultKey, readBoundVaultKey } from './vaultKeyCache';
import { createLogger } from '../../utils/logger';
import {
  SecureStorage,
  SecureStorageKeys,
} from '../storage/SecureStorage';
import {
  generateKey,
  generateSalt,
} from './DocumentEncryptionV2';
import {
  deriveKEK,
  KeyWrappingError,
  parseWrappedKey,
  serializeWrappedKey,
  unwrapKey,
  verifierFromKEK,
  wrapKey,
} from './KeyWrapping';
import {
  generateWrappingKeypair,
  unwrapWrappingSecretKey,
  wrapWrappingSecretKey,
} from './TrustedPartyWrapping';

const logger = createLogger('MasterKeyService');

const WRAP_VERSION = 'w1cbc' as const;

/**
 * Orchestrates the Step-5 master-key lifecycle.
 *
 * Ties together KeyWrapping (crypto) + vaultService (HTTP) + SecureStorage
 * (keychain) so the four high-level flows — enroll, fetch-and-unwrap,
 * change-secret-code, and silent migration — read as single functions to
 * their callers.
 *
 * IMPORTANT: none of these methods know about the *application* UX flow.
 * They're building blocks. A later change wires them to the actual "set
 * your secret code" / "unlock vault" / "change recovery code" screens.
 * Until then, the app keeps using the pre-Step-5 auto-generated-master-key
 * path in DocumentEncryptionService (see documentStorageService.ts).
 */

export class NotEnrolledError extends Error {
  constructor() {
    super('MasterKey: user has not enrolled a wrapped master key yet');
    this.name = 'NotEnrolledError';
  }
}

export class InvalidSecretCodeError extends Error {
  constructor() {
    super('MasterKey: the supplied secret code does not unwrap the stored key');
    this.name = 'InvalidSecretCodeError';
  }
}

// Called after the master key is cached on this device (unlock, enrollment,
// legacy migration). K5's credential migration listens; nothing else.
const masterKeyListeners: Array<() => void> = [];

export function onMasterKeyCached(listener: () => void): () => void {
  masterKeyListeners.push(listener);
  return () => {
    const i = masterKeyListeners.indexOf(listener);
    if (i >= 0) masterKeyListeners.splice(i, 1);
  };
}

async function cacheMasterKeyInMemory(masterKey: string): Promise<void> {
  // Device-only keychain cache for this install. Do not use biometric
  // access-control here: some valid TestFlight devices reject writes even
  // when passcode/Face ID appear enabled. The vault is still protected by
  // the recovery code on new devices. The cache is bound to this account
  // and survives its sign-out, never another account's (vaultKeyCache).
  await cacheVaultKey(SecureStorageKeys.masterRecoveryKey, masterKey);
  for (const listener of [...masterKeyListeners]) {
    try {
      listener();
    } catch (err) {
      logger.warn('master-key listener failed:', err);
    }
  }
}

async function dropCachedMasterKey(): Promise<void> {
  await SecureStorage.removeItem(SecureStorageKeys.masterRecoveryKey);
}

function cacheWrappingSecretKey(secretKey: string): Promise<void> {
  return cacheVaultKey(SecureStorageKeys.wrappingSecretKey, secretKey);
}

/**
 * First-time enrollment. Generates a fresh master key + salt, derives a KEK
 * from the user's secret code, wraps the master key, generates a fresh
 * wrapping keypair, and uploads BOTH in a single atomic backend write.
 *
 * Atomicity: the wrapped master key and the wrapping keypair persist as one
 * row update on the backend. Either both columns are set or neither is —
 * closing the half-enrollment window where a blip between two separate
 * POSTs could leave `wrapped_master_key` set but `wrapping_public_key` NULL
 * (a state the client cannot self-heal from on builds where unwrap also
 * fails — see investigation notes for the 1.7.1 PBKDF2 cohort).
 *
 * Safe to call multiple times — calling again with a different secret code
 * effectively rotates the master key (the old wrapped form on the backend
 * is overwritten). If the user already has a wrapped master key on the
 * backend and you want to *preserve* the existing master key, use
 * `changeSecretCode` instead.
 */
export async function enrollMasterKey(
  secretCode: string,
): Promise<string> {
  if (!secretCode || secretCode.length < 8) {
    throw new Error('secret code must be at least 8 characters');
  }
  const salt = await generateSalt();
  const kek = await deriveKEK(secretCode, salt);
  const masterKey = await generateKey();
  const envelope = await wrapKey(masterKey, kek);
  const verifier = await verifierFromKEK(kek);

  // Generate the wrapping keypair locally and wrap its secret half under
  // the same KEK so we can hand both pieces (master + keypair) to the
  // backend in one atomic POST.
  const kp = await generateWrappingKeypair();
  const wrappedSk = await wrapWrappingSecretKey(kp.secretKey, kek);

  await vaultService.storeWrappedMasterKeyWithKeypair({
    wrappedMasterKey: serializeWrappedKey(envelope),
    wrapVersion: WRAP_VERSION,
    verifier,
    userKdfSalt: salt,
    wrappingPublicKey: kp.publicKey,
    wrappedWrappingSecretKey: wrappedSk,
  });
  await cacheMasterKeyInMemory(masterKey);
  await cacheWrappingSecretKey(kp.secretKey);
  logger.info('Master key + wrapping keypair enrolled atomically');
  return masterKey;
}

/**
 * Generate an X25519 wrapping keypair so other users can seal this user's
 * master key for trusted-party access (medium-term #2). The secret half is
 * wrapped under the caller's KEK; the public half is non-secret.
 *
 * Idempotent in effect: POST-overwrites on subsequent calls. If an existing
 * keypair is already enrolled, callers should prefer not to rotate it —
 * rotating invalidates any outstanding sealed master-key copies other users
 * are holding and forces a handshake redo for every active relationship.
 */
export async function enrollWrappingKeypair(kek: string): Promise<void> {
  const kp = await generateWrappingKeypair();
  const wrappedSk = await wrapWrappingSecretKey(kp.secretKey, kek);
  await vaultService.storeWrappingKeypair({
    wrappingPublicKey: kp.publicKey,
    wrappedWrappingSecretKey: wrappedSk,
  });
  await cacheWrappingSecretKey(kp.secretKey);
  logger.info('Wrapping keypair enrolled');
}

/**
 * Cache the unwrapped wrapping secret key after a login that happened to
 * also derive a KEK. Callers: fetchAndUnwrapMasterKey + migrateLegacy path.
 *
 * If the user has a wrapped master key but no wrapping keypair on file,
 * lazy-enroll one under the current KEK. This covers the medium-term-2
 * rollout: users who enrolled a recovery code BEFORE trusted-party
 * wrapping shipped have `wrapping_public_key` / `wrapped_wrapping_secret_key`
 * NULL on their user row. Without this fallback they'd never get pulled
 * into the trusted-party flow — primaries inviting them would land in a
 * permanent pending-handshake state. Lazy-enroll on first post-deploy
 * login closes that gap silently; no user action needed.
 */
async function refreshCachedWrappingSecretKey(kek: string): Promise<void> {
  const stored = await vaultService.getWrappingKeypair();
  if (!stored) {
    logger.info('No wrapping keypair yet — lazy-enrolling under current KEK');
    try {
      await enrollWrappingKeypair(kek);
    } catch (err) {
      // Don't fail the login if the enrollment POST hits a transient
      // error; the next successful login will retry. A persistent failure
      // surfaces in consumer flows via TrustedPartyAccessNotReadyError.
      logger.warn('Lazy wrapping-keypair enrollment failed:', err);
    }
    return;
  }
  try {
    const sk = await unwrapWrappingSecretKey(
      stored.wrappedWrappingSecretKey,
      kek,
    );
    await cacheWrappingSecretKey(sk);
  } catch (err) {
    // A key-wrapping error here means the user's stored keypair was
    // produced under a different KEK (e.g., the keypair was never
    // re-wrapped on a past secret-code change). Log but don't fail the
    // login — consumer flows will surface a clearer error when they
    // actually need the secret key.
    logger.warn('Unable to unwrap cached wrapping secret key:', err);
  }
}

/**
 * Retrieve the user's wrapping keypair and return the unwrapped secret +
 * public halves. Used by consumer flows (successor post-death,
 * keyholder live-read) before calling `openSealedMasterKey` on a
 * primary's blob.
 *
 * Returns null when the user has not enrolled a keypair yet. Throws
 * InvalidSecretCodeError if the KEK fails to unwrap the stored blob —
 * callers should route to UnlockVaultScreen to re-derive the KEK.
 */
export async function fetchAndUnwrapWrappingKeypair(
  kek: string,
): Promise<{ publicKey: string; secretKey: string } | null> {
  const stored = await vaultService.getWrappingKeypair();
  if (!stored) return null;
  try {
    const secretKey = await unwrapWrappingSecretKey(
      stored.wrappedWrappingSecretKey,
      kek,
    );
    return { publicKey: stored.wrappingPublicKey, secretKey };
  } catch (err) {
    if (err instanceof KeyWrappingError) {
      throw new InvalidSecretCodeError();
    }
    throw err;
  }
}

/**
 * Convenience: return just the user's published wrapping public key, so
 * AcceptInvitationScreen can submit it as part of an invite acceptance.
 * Returns null if the user has not enrolled a recovery code yet (no
 * wrapping keypair exists to publish).
 */
export async function getWrappingPublicKey(): Promise<string | null> {
  const stored = await vaultService.getWrappingKeypair();
  return stored?.wrappingPublicKey ?? null;
}

/**
 * New-device login — the salt + wrapped key come from the backend.
 * Throws NotEnrolledError when the user has no wrapped key yet (caller
 * may then route to the migration or enrollment flow).
 * Throws InvalidSecretCodeError when the stored envelope fails to unwrap
 * under a KEK derived from the supplied secret code.
 */
export async function fetchAndUnwrapMasterKey(
  secretCode: string,
): Promise<string> {
  if (!secretCode) throw new Error('secret code is required');

  const stored = await vaultService.getWrappedMasterKey();
  if (!stored) throw new NotEnrolledError();

  const kek = await deriveKEK(secretCode, stored.userKdfSalt);
  let masterKey: string;
  try {
    const envelope = parseWrappedKey(stored.wrappedMasterKey);
    masterKey = await unwrapKey(envelope, kek);
    await cacheMasterKeyInMemory(masterKey);
    logger.info('Master key unwrapped and cached');
  } catch (err) {
    if (err instanceof KeyWrappingError) {
      throw new InvalidSecretCodeError();
    }
    throw err;
  }
  // Best-effort refresh of the cached wrapping secret key — used by
  // trusted-party consumer flows. Doesn't block the return value.
  await refreshCachedWrappingSecretKey(kek);
  return masterKey;
}

/**
 * Change secret code without revealing either secret code or the master
 * key to the backend. Proves knowledge of the old code client-side
 * (unwraps locally), then re-wraps under the new KEK and POSTs with both
 * verifiers so the backend can authorize the overwrite.
 *
 * Throws InvalidSecretCodeError if `oldSecretCode` does not unwrap the
 * stored envelope — the backend is never asked in that case.
 */
export async function changeSecretCode(
  oldSecretCode: string,
  newSecretCode: string,
): Promise<void> {
  if (!newSecretCode || newSecretCode.length < 8) {
    throw new Error('new secret code must be at least 8 characters');
  }
  const stored = await vaultService.getWrappedMasterKey();
  if (!stored) throw new NotEnrolledError();

  const oldKek = await deriveKEK(oldSecretCode, stored.userKdfSalt);
  let masterKey: string;
  try {
    masterKey = await unwrapKey(parseWrappedKey(stored.wrappedMasterKey), oldKek);
  } catch (err) {
    if (err instanceof KeyWrappingError) throw new InvalidSecretCodeError();
    throw err;
  }

  const newKek = await deriveKEK(newSecretCode, stored.userKdfSalt);
  const newEnvelope = await wrapKey(masterKey, newKek);
  const oldVerifier = await verifierFromKEK(oldKek);
  const newVerifier = await verifierFromKEK(newKek);

  await vaultService.changeWrappedMasterKey({
    oldVerifier,
    newWrappedMasterKey: serializeWrappedKey(newEnvelope),
    newWrapVersion: WRAP_VERSION,
    newVerifier,
  });

  // Re-wrap the trusted-party wrapping keypair under the new KEK so the
  // user doesn't lose access to sealed blobs other primaries may have
  // issued for them. The public half is unchanged — we keep the existing
  // pubkey so other primaries' wraps stay valid.
  const existingKeypair = await vaultService.getWrappingKeypair();
  if (existingKeypair) {
    try {
      const secretKey = await unwrapWrappingSecretKey(
        existingKeypair.wrappedWrappingSecretKey,
        oldKek,
      );
      const reWrapped = await wrapWrappingSecretKey(secretKey, newKek);
      await vaultService.storeWrappingKeypair({
        wrappingPublicKey: existingKeypair.wrappingPublicKey,
        wrappedWrappingSecretKey: reWrapped,
      });
    } catch (err) {
      // If the existing wrapped keypair can't be opened under the old KEK
      // something is badly wrong (stored wrapping was never correct or
      // already mismatched). Generate a fresh keypair so the user can still
      // receive FUTURE wraps. Outstanding ones issued for the old pubkey
      // are lost — log so we notice, but don't block the secret-code rotation.
      logger.warn(
        'Existing wrapping keypair could not be re-wrapped under new KEK — regenerating',
      );
      await enrollWrappingKeypair(newKek);
    }
  }

  await cacheMasterKeyInMemory(masterKey);
  logger.info('Master key rotated (secret code changed)');
}

/**
 * Silent migration for a user who enrolled BEFORE Step 5 — their master
 * key lives unwrapped in the backend Vault via the legacy
 * `/document-keys/master` path. On next login (once the user supplies a
 * secret code) we pull the cleartext master key, wrap it under a KEK,
 * and POST the wrapped triple. The legacy Vault copy remains until Step
 * 5.5 cleans it up server-side.
 *
 * Returns the master key so the caller can proceed with the normal
 * post-login flow. Throws if the legacy key cannot be retrieved (e.g.,
 * user never enrolled one — they may be a brand-new signup who should
 * go through enrollMasterKey instead).
 */
export async function migrateLegacyMasterKey(
  secretCode: string,
): Promise<string> {
  if (!secretCode || secretCode.length < 8) {
    throw new Error('secret code must be at least 8 characters');
  }
  const legacyMasterKey = await vaultService.getMasterRecoveryKey();
  if (!legacyMasterKey) {
    throw new Error(
      'MasterKey: no legacy master key to migrate — enroll first',
    );
  }
  const salt = await generateSalt();
  const kek = await deriveKEK(secretCode, salt);
  const envelope = await wrapKey(legacyMasterKey, kek);
  const verifier = await verifierFromKEK(kek);

  // Same atomicity guarantee as first-time enrollment: wrapped master key
  // and wrapping keypair persist together or not at all.
  const kp = await generateWrappingKeypair();
  const wrappedSk = await wrapWrappingSecretKey(kp.secretKey, kek);

  await vaultService.storeWrappedMasterKeyWithKeypair({
    wrappedMasterKey: serializeWrappedKey(envelope),
    wrapVersion: WRAP_VERSION,
    verifier,
    userKdfSalt: salt,
    wrappingPublicKey: kp.publicKey,
    wrappedWrappingSecretKey: wrappedSk,
  });
  await cacheMasterKeyInMemory(legacyMasterKey);
  await cacheWrappingSecretKey(kp.secretKey);
  logger.info('Legacy master key migrated to wrapped form (atomic)');
  return legacyMasterKey;
}

/**
 * Returns true if the user has enrolled a wrapped master key on the
 * backend. Used by the app on startup to decide whether to route to
 * the secret-code-entry screen vs. the legacy auto-generated-key path.
 */
export async function isWrappedMasterKeyEnrolled(): Promise<boolean> {
  const stored = await vaultService.getWrappedMasterKey();
  return stored !== null;
}

/**
 * Returns the unwrapped master key if it's currently cached on the
 * device (previously unwrapped via enrollMasterKey /
 * fetchAndUnwrapMasterKey / migrateLegacyMasterKey and not dropped by a
 * logout or an accessibility trigger). Returns null if no cached key
 * exists — the caller should route to UnlockVaultScreen in that case.
 *
 * Does NOT prompt biometry here; the key is stored under
 * BIOMETRY_CURRENT_SET so the underlying SecureStorage read will
 * trigger the OS prompt if needed. A user who cancels the prompt gets
 * null back; treat that as a soft "locked" state.
 */
export async function getCachedMasterKey(): Promise<string | null> {
  return readBoundVaultKey(SecureStorageKeys.masterRecoveryKey);
}

/**
 * Returns the unwrapped wrapping secret key if it's cached on this device.
 * Populated at login / enrollment so trusted-party consumer flows
 * (open sealed master-key blobs for successor / keyholder access) don't
 * need a fresh PBKDF2 each time. Null when nothing is cached — the
 * caller should route the user to unlock first.
 */
export async function getCachedWrappingSecretKey(): Promise<string | null> {
  return readBoundVaultKey(SecureStorageKeys.wrappingSecretKey);
}

/**
 * True when the user has enrolled AND the unwrapped key is cached — the
 * document-encryption path is safe to use their wrapped master key
 * directly. False when either: (a) not enrolled (legacy path applies)
 * or (b) enrolled but cache is empty (UnlockVaultScreen needed first).
 *
 * Callers that need to distinguish (a) from (b) should call
 * `isWrappedMasterKeyEnrolled()` + `getCachedMasterKey()` separately.
 */
export async function isVaultReadyToUse(): Promise<boolean> {
  const [enrolled, cached] = await Promise.all([
    isWrappedMasterKeyEnrolled(),
    getCachedMasterKey(),
  ]);
  return enrolled && cached !== null;
}

/**
 * Raised by DocumentEncryptionService.getMasterRecoveryKey when the
 * user has enrolled a wrapped master key but no unwrapped copy is
 * cached on this device. The caller should route to UnlockVaultScreen.
 *
 * This is a control-flow error, not a user-facing message — the
 * screen copy comes from the unlock UI itself.
 */
export class VaultLockedError extends Error {
  constructor() {
    super(
      'Vault is locked. Enter your recovery code to unlock it on this device.',
    );
    this.name = 'VaultLockedError';
  }
}

/**
 * Raised when a document is about to be saved by an account that has not
 * set a Recovery Code (2026-09-25). Documents are kept only under a key
 * wrapped with the Recovery Code, so FinalKey cannot read them; setting the
 * code wraps the key the account already has, so nothing is lost. The
 * caller should offer the Set Recovery Code flow.
 */
export class RecoveryCodeRequiredError extends Error {
  constructor() {
    super('Set a Recovery Code to keep documents in FinalKey.');
    this.name = 'RecoveryCodeRequiredError';
  }
}

export const __internal = {
  cacheMasterKeyInMemory,
  dropCachedMasterKey,
};
